// The chef bar — the assistant's single input line, always at the bottom of
// the app in every mode. Styled like a command line (mono face, "❯" prompt)
// after the Claude message bar: text input, mic button (voice comes later),
// send pill. While a reply is pending or being "spoken", the three-bar
// speaking indicator (index.css .chef-speaking) pulses beside the prompt.
//
// Prototype scope: posts to /api/chef, which returns a canned reply until the
// Agent SDK lands. The reply shows as a dismissible card floating above the
// bar; the input mechanism, layout and indicator are final.

import { useRef, useState } from "react";
import { ArrowUp, Mic, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { playClick } from "@/sounds";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export function ChefBar() {
  const [draft, setDraft] = useState("");
  const [reply, setReply] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  async function send() {
    const message = draft.trim();
    if (!message || pending) return;
    playClick();
    setDraft("");
    setPending(true);
    try {
      const res = await fetch("/api/chef", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message }),
      });
      const data = res.ok ? await res.json() : null;
      setReply(data?.reply ?? "The chef is not in the kitchen yet — backend unreachable.");
    } catch {
      setReply("The chef is not in the kitchen yet — backend unreachable.");
    } finally {
      setPending(false);
      inputRef.current?.focus();
    }
  }

  return (
    <div className="relative shrink-0">
      {/* Reply card, floating above the bar. */}
      {reply !== null && (
        <div className="absolute inset-x-3 bottom-[calc(100%+8px)] z-20 flex items-start gap-3 rounded-xl border border-line-strong bg-popover p-3.5 text-popover-foreground shadow-[0_8px_30px_rgba(0,0,0,0.15)]">
          <span className="mt-0.5 shrink-0 font-mono text-xs text-brand-ink select-none">chef</span>
          <p className="min-w-0 flex-1 text-sm">{reply}</p>
          <button
            type="button"
            aria-label="Dismiss reply"
            onClick={() => {
              setReply(null);
              playClick();
            }}
            className="shrink-0 cursor-pointer rounded-md p-1 text-dim transition-colors hover:bg-panel hover:text-foreground"
          >
            <X className="size-3.5" />
          </button>
        </div>
      )}

      {/* The bar itself: a bit taller than the header, command-line styled. */}
      <div className="flex h-[var(--chefbar-height)] items-center gap-2.5 border-t border-line bg-surface px-3.5">
        {/* Prompt / speaking indicator. */}
        <div className="flex w-6 shrink-0 items-center justify-center">
          {pending ? (
            <span className="chef-speaking" aria-label="The chef is thinking">
              <span />
              <span />
              <span />
            </span>
          ) : (
            <span aria-hidden className="font-mono text-sm font-semibold text-brand-ink select-none">
              ❯
            </span>
          )}
        </div>

        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void send();
          }}
          placeholder='ask the chef, or say "yes, chef"'
          aria-label="Message the chef"
          className="min-w-0 flex-1 bg-transparent font-mono text-sm text-foreground outline-none placeholder:text-dimmer"
        />

        {/* Mic — placeholder until voice mode lands. */}
        <Tooltip>
          <TooltipTrigger asChild>
            <span tabIndex={-1}>
              <button
                type="button"
                disabled
                aria-label="Voice mode (coming soon)"
                className="flex size-[var(--control)] shrink-0 cursor-not-allowed items-center justify-center rounded-full text-dimmer"
              >
                <Mic className="size-[calc(var(--control)*0.5)]" />
              </button>
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" align="end">
            Voice mode comes with the agent
          </TooltipContent>
        </Tooltip>

        {/* Send — the Epicure CTA pill, round. */}
        <button
          type="button"
          onClick={() => void send()}
          disabled={!draft.trim() || pending}
          aria-label="Send"
          className={cn(
            "flex size-[var(--control)] shrink-0 cursor-pointer items-center justify-center rounded-full",
            "bg-brand text-primary-foreground transition-[transform,background-color,opacity] duration-500 ease-epicure",
            "hover:-translate-y-0.5 hover:bg-[var(--brand-hover)] active:scale-95",
            "disabled:cursor-default disabled:opacity-35 disabled:hover:translate-y-0",
          )}
        >
          <ArrowUp className="size-[calc(var(--control)*0.5)]" />
        </button>
      </div>
    </div>
  );
}
